import { h } from '@stencil/core';
import cn from 'classnames';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { RANGES_PLACEHOLDER, ICONS_BY_TYPE, ACTION_BLOCK_TYPES } from '@pes-ui/utils';
import { isActionBlockArray } from './guards';
import { getGridItemsComposition, isTopCategoryCardLarge } from './utils';
const CARD_OFFSET_WIDTH = 168;
export class TopCategories {
  constructor() {
    this.createSliderRef = (el) => {
      this.sliderRef = el;
    };
    this.resizeWindow = () => {
      this.cardsPerSlide = this.calcItemsPerSlide();
    };
    this.renderActionBlock = (item) => {
      if (!isEmpty(item.selectorConfig)) {
        return (h("pes-action-block-product-selector", { "data-autotests-id": item.type, actionBlock: item, selectorConfig: this.selectorConfig, class: "top-categories__action-block" }));
      }
      return (h("pes-action-block", { "data-autotests-id": item.type, heading: item.label, text: item.linkText, url: item.link, icon: ICONS_BY_TYPE[item.type], class: "top-categories__action-block" }));
    };
    this.list = [];
    this.viewOnLabel = undefined;
    this.cardCountLabel = undefined;
    this.cardCountPlaceholder = RANGES_PLACEHOLDER;
    this.isMobile = undefined;
    this.actionBlocks = undefined;
    this.isActive = false;
    this.hideDownloadCenterBanner = false;
    this.hideMasterRangesBanner = false;
    this.selectorConfig = null;
    this.breadcrumbs = undefined;
    this.storageName = undefined;
    this.isPBS = false;
    this.filteredActionBlocks = {};
    this.cardsPerSlide = undefined;
    this.el.dir = document.documentElement.dir || 'auto';
  }
  actionBlocksWatcher() {
    this.updateFilteredActionBlocksState();
  }
  componentWillLoad() {
    this.updateFilteredActionBlocksState();
    window.addEventListener('resize', this.resizeWindow);
  }
  componentDidLoad() {
    this.cardsPerSlide = this.calcItemsPerSlide();
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeWindow);
  }
  updateFilteredActionBlocksState() {
    const actionBlocksToOmit = [
      this.hideDownloadCenterBanner && ACTION_BLOCK_TYPES.DOWNLOAD_CENTER,
      this.hideMasterRangesBanner && ACTION_BLOCK_TYPES.MASTER_RANGE_PRODUCT,
    ].filter(Boolean);
    this.filteredActionBlocks = omit(this.actionBlocks, actionBlocksToOmit);
  }
  calcItemsPerSlide() {
    if (this.sliderRef) {
      return this.sliderRef.offsetWidth / CARD_OFFSET_WIDTH;
    }
  }
  renderCard(category, isCardLarge = false) {
    const key = `${category.id}${this.isActive ? '_active' : ''}`;
    return (h("pes-card", { key: key, page: "category", item: category, viewOnLabel: this.viewOnLabel, labelTemplate: this.cardCountLabel, labelPlaceholder: this.cardCountPlaceholder, size: isCardLarge && 'large', breadcrumbs: this.breadcrumbs, storageName: this.storageName }));
  }
  renderList() {
    const grid = getGridItemsComposition(this.list, this.filteredActionBlocks, this.isPBS);
    return grid === null || grid === void 0 ? void 0 : grid.map((item, index) => {
      if (isActionBlockArray(item)) {
        return h("div", { class: "top-categories__card" }, item === null || item === void 0 ? void 0 : item.map(this.renderActionBlock));
      }
      const isCardLarge = isTopCategoryCardLarge(this.list.length, this.filteredActionBlocks, index, this.isPBS);
      return (h("div", { class: cn('top-categories__card', { 'top-categories__card--large': isCardLarge }) }, this.renderCard(item, isCardLarge)));
    });
  }
  render() {
    var _a;
    if (this.isMobile) {
      return (h("section", { class: "top-categories" }, h("div", { class: "top-categories__slider" }, h("pdp-slider", { ref: this.createSliderRef, class: "media-gallery-mobile", childrenClassName: ".mobile-gallery-slide", itemsPerSlide: this.cardsPerSlide, showPointers: true, showButtons: false, smallPoint: true }, (_a = this.list) === null || _a === void 0 ? void 0 : _a.map((category) => this.renderCard(category))))));
    }
    return h("section", { class: "top-categories" }, this.renderList());
  }
  static get is() { return "pes-top-categories"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["top-categories.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["top-categories.css"]
    };
  }
  static get properties() {
    return {
      "list": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "ProductsAllTopCategories[]",
          "resolved": "ProductsAllTopCategories[]",
          "references": {
            "ProductsAllTopCategories": {
              "location": "import",
              "path": "../../../generated/graphql"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "defaultValue": "[]"
      },
      "viewOnLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "view-on-label",
        "reflect": false
      },
      "cardCountLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "card-count-label",
        "reflect": false
      },
      "cardCountPlaceholder": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "card-count-placeholder",
        "reflect": false,
        "defaultValue": "RANGES_PLACEHOLDER"
      },
      "isMobile": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-mobile",
        "reflect": false
      },
      "actionBlocks": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "CategoriesApi.ActionBlocksHashMap",
          "resolved": "{ [x: string]: ActionBlock; }",
          "references": {
            "CategoriesApi": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "isActive": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-active",
        "reflect": false,
        "defaultValue": "false"
      },
      "hideDownloadCenterBanner": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "hide-download-center-banner",
        "reflect": false,
        "defaultValue": "false"
      },
      "hideMasterRangesBanner": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "hide-master-ranges-banner",
        "reflect": false,
        "defaultValue": "false"
      },
      "selectorConfig": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TScProductSelectorConfig | null",
          "resolved": "{ type: \"sc\"; scConfiguratorUrlJS: string; scConfiguratorUrlCSS: string; scLanguage: string; scCountry: string; scToken: string; scProject: string; scDataset: string; scOptions: Record<string, unknown>; scFunctionalIds: string; scLabel: string; }",
          "references": {
            "TScProductSelectorConfig": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "defaultValue": "null"
      },
      "breadcrumbs": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TBreadcrumbs[]",
          "resolved": "TBreadcrumbs[]",
          "references": {
            "TBreadcrumbs": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "breadcrumbs array for saving in indexDB"
        }
      },
      "storageName": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "storageName in indexDB for saving breadcrumbs"
        },
        "attribute": "storage-name",
        "reflect": false
      },
      "isPBS": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Defines whether the component is integrated to PBS."
        },
        "attribute": "is-p-b-s",
        "reflect": false,
        "defaultValue": "false"
      }
    };
  }
  static get states() {
    return {
      "filteredActionBlocks": {},
      "cardsPerSlide": {}
    };
  }
  static get elementRef() { return "el"; }
  static get watchers() {
    return [{
        "propName": "actionBlocks",
        "methodName": "actionBlocksWatcher"
      }, {
        "propName": "hideDownloadCenterBanner",
        "methodName": "actionBlocksWatcher"
      }, {
        "propName": "hideMasterRangesBanner",
        "methodName": "actionBlocksWatcher"
      }];
  }
}
